import {Button, Modal} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Code} from 'src/design/atoms/Code';
import {Paper} from 'src/design/atoms/Paper';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {useClipboard, useTimeout} from '@mantine/hooks';
import {IconAlertTriangle, IconCheck, IconCopy} from '@tabler/icons-react';
import React, {useEffect, useState} from 'react';
import type {IntegrationTokenCreateResponse} from 'src/api/ProactApiClient';
import {ConfirmDialog} from 'src/design/molecules/ConfirmDialog';

export interface TokenRevealModalProps {
  opened: boolean;
  token: IntegrationTokenCreateResponse | null;
  onAcknowledge: () => void;
}

const ACKNOWLEDGE_DELAY_MS = 3000;

export const TokenRevealModal: React.FC<TokenRevealModalProps> = ({
  opened,
  token,
  onAcknowledge,
}) => {
  const clipboard = useClipboard({timeout: 3000});
  const [canAcknowledge, setCanAcknowledge] = useState(false);
  const [showUncopyConfirm, setShowUncopyConfirm] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  const {start: startAcknowledgeTimer} = useTimeout(
    () => setCanAcknowledge(true),
    ACKNOWLEDGE_DELAY_MS
  );

  useEffect(() => {
    if (opened) {
      setCanAcknowledge(false);
      setHasCopied(false);
      clipboard.reset();
      startAcknowledgeTimer();
    }
    // clipboard and startAcknowledgeTimer are stable refs — intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const handleCopy = () => {
    if (token?.rawToken) {
      clipboard.copy(token.rawToken);
      setHasCopied(true);
    }
  };

  const handleAcknowledge = () => {
    if (!hasCopied) {
      setShowUncopyConfirm(true);
    } else {
      onAcknowledge();
    }
  };

  const handleConfirmAcknowledgeWithoutCopy = () => {
    setShowUncopyConfirm(false);
    onAcknowledge();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={() => {}}
        title="Integration Token Created"
        closeOnClickOutside={false}
        closeOnEscape={false}
        withCloseButton={false}
        styles={{title: {fontWeight: 700}}}
        centered
        size="lg">
        <Stack gap="md">
          <Alert
            color="red"
            variant="light"
            icon={<IconAlertTriangle size={16} />}
            aria-live="assertive">
            This token will only be shown once. It cannot be retrieved after you
            close this dialog. Copy it now and store it securely.
          </Alert>

          {token?.description && (
            <Text size="sm" c="dimmed">
              Token for:{' '}
              <Text span fw={600} c="dark">
                {token.description}
              </Text>
            </Text>
          )}

          <Paper bg="gray.0" p="sm" withBorder>
            <Code
              block
              style={{
                wordBreak: 'break-all',
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: 13,
                background: 'transparent',
              }}>
              {token?.rawToken ?? ''}
            </Code>
          </Paper>

          <Button
            variant="outline"
            size="sm"
            color={clipboard.copied ? 'green' : 'dark'}
            leftSection={
              clipboard.copied ? (
                <IconCheck size={16} />
              ) : (
                <IconCopy size={16} />
              )
            }
            aria-label="Copy token to clipboard"
            onClick={handleCopy}>
            Copy Token
          </Button>

          <Button
            variant="filled"
            color="yellow"
            fullWidth
            size="sm"
            mt="lg"
            loading={!canAcknowledge}
            loaderProps={{size: 'xs'}}
            onClick={handleAcknowledge}>
            I have saved this token
          </Button>
        </Stack>
      </Modal>

      <ConfirmDialog
        opened={showUncopyConfirm}
        onClose={() => setShowUncopyConfirm(false)}
        onConfirm={handleConfirmAcknowledgeWithoutCopy}
        title="Token not copied"
        body="You haven't copied the token yet. Are you sure you want to close? The token will not be shown again."
        confirmationButtonColor="red"
        buttonTextYes="Yes, I understand"
        buttonTextNo="Go back"
      />
    </>
  );
};
